import { API_ERROR_CODES, getProtectedRouteAuthErrorMessage, parseApiError } from "@/lib/api/api-error";
import type {
  AccessRequest,
  AccessScope,
  ApiPlatform,
  CreateAccessRequestPayload,
  KeyEnvironment,
  RequesterType,
} from "@/lib/api/access-requests";
import type { ExpectedDailyVolume } from "@/lib/api/login-user";
import {
  ACCESS_ROLE_OPTIONS,
  type AccessRole,
} from "@/lib/api/portal-access-role";

export const ACCESS_REQUEST_FIELD_LIMITS = {
  intendedUseCaseMin: 10,
  intendedUseCaseMax: 5000,
  scopeListMax: 100,
  scopeItemMax: 255,
} as const;

export const ACCESS_REQUEST_ERROR_CODES = {
  duplicateRequest: "accessrequests.duplicate_request",
  scopeRequired: "accessrequests.scope_required",
  scopeMixedNotAllowed: "accessrequests.scope_mixed_not_allowed",
  invalidScopeFormat: "accessrequests.invalid_scope_format",
  invalidAccessScope: "accessrequests.invalid_access_scope",
  scopeNotAllowedForSandbox: "accessrequests.scope_not_allowed_for_sandbox",
  locationIdsNotSupported: "accessrequests.location_ids_not_supported",
  unauthorized: "accessrequests.unauthorized",
  invalidContestation: "accessrequests.invalid_contestation",
  forbidden: "accessrequests.forbidden",
  notFound: "accessrequests.not_found",
  illegalStatusTransition: "accessrequests.illegal_status_transition",
  integratorNameRequired: "accessrequests.integrator_name_required",
} as const;

export type AccessIntegration = "api" | "zapier";
export type AccessScopeType = AccessScope;

export const EXPECTED_DAILY_VOLUME_OPTIONS: {
  value: ExpectedDailyVolume;
  label: string;
}[] = [
  { value: "under_1k", label: "Under 1k" },
  { value: "1k_10k", label: "1k – 10k" },
  { value: "10k_100k", label: "10k – 100k" },
  { value: "100k_plus", label: "100k+" },
];

export const REQUESTER_TYPE_OPTIONS: {
  value: RequesterType;
  label: string;
  description: string;
}[] = [
  {
    value: "customer",
    label: "Customer",
    description: "Requesting access for your own studio or business.",
  },
  {
    value: "partner",
    label: "Partner",
    description: "Building on behalf of one or more Glofox customers.",
  },
];

export const ACCESS_SCOPE_OPTIONS: {
  value: AccessScopeType;
  label: string;
}[] = [
  { value: "namespaces", label: "Namespaces" },
  { value: "locations", label: "Locations" },
];

export const INTEGRATION_OPTIONS: {
  value: AccessIntegration;
  label: string;
}[] = [
  { value: "api", label: "API - default" },
  { value: "zapier", label: "Zapier" },
];

export interface AccessRequestFormValues {
  /** Legacy Request Access only (Slice 6 off). Slice 6 uses portal access_role. */
  requesterType?: RequesterType;
  accessScopeType: AccessScopeType;
  intendedUseCase: string;
  locationIdsText: string;
  namespacesText: string;
  expectedDailyVolume: ExpectedDailyVolume | "";
  integration: AccessIntegration | "";
}

export type { AccessRole };
export { ACCESS_ROLE_OPTIONS };

export function getPlatformRequests(
  requests: AccessRequest[],
  platform: ApiPlatform,
): AccessRequest[] {
  return requests.filter((request) => request.api_platform === platform);
}

export function isIntegrationRequested(
  requests: AccessRequest[],
  integration: AccessIntegration,
): boolean {
  return requests.some((request) => {
    if (integration === "zapier") {
      return request.integrations.includes("zapier");
    }

    return (
      request.integrations.length === 0 || request.integrations.includes("api")
    );
  });
}

export function getAvailableIntegrations(
  requests: AccessRequest[],
  platform: ApiPlatform,
): AccessIntegration[] {
  const platformRequests = getPlatformRequests(requests, platform);
  return INTEGRATION_OPTIONS.map((option) => option.value).filter(
    (value) => !isIntegrationRequested(platformRequests, value),
  );
}

export function canRequestAnotherAccess(
  requests: AccessRequest[],
  platform: ApiPlatform,
): boolean {
  return getAvailableIntegrations(requests, platform).length > 0;
}

export function formatIntegrationDisplay(integration: string): string {
  if (integration === "api") {
    return "API";
  }
  if (integration === "zapier") {
    return "Zapier";
  }
  return integration;
}

export function formatAccessScopeLabel(
  scope: AccessScope | null | undefined,
): string {
  if (scope === "namespaces") {
    return "Namespaces";
  }
  if (scope === "locations") {
    return "Locations";
  }
  return "Not provided (legacy request)";
}

/** Admin detail scope line: Sandbox does not require access scope. */
export function formatAdminAccessScopeLabel(
  environment: KeyEnvironment | null | undefined,
  scope: AccessScope | null | undefined,
): string {
  if (environment === "sandbox") {
    return "Not required";
  }
  return formatAccessScopeLabel(scope);
}

export function getRequestIntegrationsDisplay(request: AccessRequest): string[] {
  if (request.integrations.length === 0) {
    return ["API"];
  }

  return request.integrations.map((integration) =>
    formatIntegrationDisplay(integration),
  );
}

export function validateScopeText(value: string): string | null {
  if (/[\n\r]/.test(value)) {
    return "Use comma-separated values only. New lines are not allowed.";
  }
  return null;
}

export function parseScopeListInput(value: string): string[] {
  const seen = new Set<string>();
  const items: string[] = [];

  for (const part of value.split(",")) {
    const trimmed = part.trim();
    if (!trimmed || trimmed.length > ACCESS_REQUEST_FIELD_LIMITS.scopeItemMax) {
      continue;
    }
    const key = trimmed.toLowerCase();
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    items.push(trimmed);
    if (items.length >= ACCESS_REQUEST_FIELD_LIMITS.scopeListMax) {
      break;
    }
  }

  return items;
}

export function getActiveScopeText(form: AccessRequestFormValues): string {
  return form.accessScopeType === "locations"
    ? form.locationIdsText
    : form.namespacesText;
}

export function hasScope(form: AccessRequestFormValues): boolean {
  return parseScopeListInput(getActiveScopeText(form)).length > 0;
}

export function isAccessRequestFormValid(
  form: AccessRequestFormValues,
  availableIntegrations: AccessIntegration[],
): boolean {
  const intendedUseCase = form.intendedUseCase.trim();

  if (
    intendedUseCase.length < ACCESS_REQUEST_FIELD_LIMITS.intendedUseCaseMin ||
    intendedUseCase.length > ACCESS_REQUEST_FIELD_LIMITS.intendedUseCaseMax
  ) {
    return false;
  }

  if (
    form.requesterType !== undefined &&
    form.requesterType !== "customer" &&
    form.requesterType !== "partner"
  ) {
    return false;
  }

  if (!form.expectedDailyVolume) {
    return false;
  }

  if (
    !form.integration ||
    !availableIntegrations.includes(form.integration)
  ) {
    return false;
  }

  if (validateScopeText(getActiveScopeText(form))) {
    return false;
  }

  if (!hasScope(form)) {
    return false;
  }

  return true;
}

export function toCreateAccessRequestPayload(
  form: AccessRequestFormValues,
  apiPlatform: ApiPlatform,
): CreateAccessRequestPayload {
  const locationIds =
    form.accessScopeType === "locations"
      ? parseScopeListInput(form.locationIdsText)
      : [];
  const namespaces =
    form.accessScopeType === "namespaces"
      ? parseScopeListInput(form.namespacesText)
      : [];

  return {
    api_platform: apiPlatform,
    ...(form.requesterType ? { requester_type: form.requesterType } : {}),
    access_scope: form.accessScopeType,
    intended_use_case: form.intendedUseCase.trim(),
    location_ids: locationIds,
    namespaces,
    expected_daily_volume: form.expectedDailyVolume as ExpectedDailyVolume,
    integrations: form.integration ? [form.integration] : [],
  };
}

const SUBMIT_MESSAGES = {
  network:
    "We couldn't reach the server. Check your connection and try again.",
  validation:
    "Please check your details and try again. Make sure all required fields are filled in correctly.",
  scopeRequired: "Access scope is required.",
  scopeMixedNotAllowed: "Provide either namespaces or locations, not both.",
  invalidScopeFormat:
    "Use comma-separated values only. New lines are not allowed.",
  invalidAccessScope: "Invalid access scope.",
  duplicate:
    "You have already submitted an access request for this integration.",
  unauthorized: "Your session has expired. Please log in again.",
  server:
    "Something went wrong on our side. Please try again in a few minutes.",
  generic: "Something went wrong. Please try again later.",
} as const;

export function getAccessRequestSubmitErrorMessage(err: unknown): string {
  const { status, code, message, isNetworkError } = parseApiError(err);

  if (isNetworkError) {
    return SUBMIT_MESSAGES.network;
  }

  const authMessage = getProtectedRouteAuthErrorMessage(code, message);
  if (status === 401 || authMessage) {
    return authMessage ?? SUBMIT_MESSAGES.unauthorized;
  }

  if (
    status === 409 &&
    code === ACCESS_REQUEST_ERROR_CODES.duplicateRequest
  ) {
    return SUBMIT_MESSAGES.duplicate;
  }

  if (status === 400) {
    switch (code) {
      case ACCESS_REQUEST_ERROR_CODES.scopeRequired:
        return SUBMIT_MESSAGES.scopeRequired;
      case ACCESS_REQUEST_ERROR_CODES.scopeMixedNotAllowed:
        return SUBMIT_MESSAGES.scopeMixedNotAllowed;
      case ACCESS_REQUEST_ERROR_CODES.invalidScopeFormat:
        return SUBMIT_MESSAGES.invalidScopeFormat;
      case ACCESS_REQUEST_ERROR_CODES.invalidAccessScope:
        return SUBMIT_MESSAGES.invalidAccessScope;
      default:
        return SUBMIT_MESSAGES.validation;
    }
  }

  if (
    status === 500 ||
    code === API_ERROR_CODES.internalServerError
  ) {
    return SUBMIT_MESSAGES.server;
  }

  return SUBMIT_MESSAGES.generic;
}

export function getAccessRequestLoadErrorMessage(err: unknown): string {
  const { status, code, message, isNetworkError } = parseApiError(err);

  if (isNetworkError) {
    return SUBMIT_MESSAGES.network;
  }

  const authMessage = getProtectedRouteAuthErrorMessage(code, message);
  if (status === 401 || authMessage) {
    return authMessage ?? SUBMIT_MESSAGES.unauthorized;
  }

  if (status === 500) {
    return SUBMIT_MESSAGES.server;
  }

  return SUBMIT_MESSAGES.generic;
}
